import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export const useAccountDeletion = () => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const { user } = useAuth();
  const { t } = useTranslation('profile');

  const exportUserData = async () => {
    if (!user) throw new Error('No user found');
    
    setIsExporting(true);
    try {
      // Fetch user's timeline entries
      const { data: entries, error: entriesError } = await supabase
        .from('profile_timeline_entries')
        .select(`
          *,
          profile_timeline_sections (
            name,
            name_en,
            template_timeline_section_id
          )
        `)
        .eq('user_id', user.id);

      if (entriesError) throw entriesError;

      // Fetch user's profile data
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;

      // Fetch user's managed profiles
      const { data: managedProfiles, error: managedError } = await supabase
        .from('profile_ownerships')
        .select(`
          relationship_type,
          created_at,
          profiles (
            username,
            first_name,
            last_name,
            birthdate
          )
        `)
        .eq('owner_id', user.id);

      if (managedError) throw managedError;

      const exportData = {
        export_date: new Date().toISOString(),
        user_email: user.email,
        profile,
        timeline_entries: entries,
        managed_profiles: managedProfiles,
        export_info: {
          description: 'Complete export of your uplivion data',
          total_entries: entries?.length || 0,
          managed_profiles_count: managedProfiles?.length || 0
        }
      };

      // Create and download JSON file
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `uplivion-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(t('account.exportSuccess', 'Data exported successfully'));
    } catch (error) {
      console.error('Export error:', error);
      toast.error(t('account.exportError', 'Failed to export data'));
      throw error;
    } finally {
      setIsExporting(false);
    }
  };

  const deleteAccount = async (confirmationEmail: string, password: string) => {
    if (!user) throw new Error('No user found');
    
    if (confirmationEmail !== user.email) {
      throw new Error(t('account.emailMismatch', 'Email confirmation does not match your account email'));
    }

    setIsDeleting(true);
    try {
      // Re-authenticate user with password
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: user.email!,
        password: password
      });

      if (authError) {
        throw new Error(t('account.invalidPassword', 'Invalid password'));
      }

      // Call the comprehensive deletion function
      const { data, error } = await supabase.rpc('delete_user_completely', {
        target_user_id: user.id
      });

      if (error) throw error;

      const result = data as any;
      if (!result?.success) {
        throw new Error(result?.error || 'Deletion failed');
      }

      toast.success(t('account.deleteSuccess', 'Account deleted successfully'));
      
      // Sign out and redirect
      await supabase.auth.signOut();
      window.location.href = 'https://www.uplivion.life';
      
      return data;
    } catch (error) {
      console.error('Account deletion error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete account';
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsDeleting(false);
    }
  };

  const anonymizeAccount = async (confirmationEmail: string, password: string) => {
    if (!user) throw new Error('No user found');
    
    if (confirmationEmail !== user.email) {
      throw new Error(t('account.emailMismatch', 'Email confirmation does not match your account email'));
    }

    setIsDeleting(true);
    try {
      // Re-authenticate user
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: user.email!,
        password: password
      });

      if (authError) {
        throw new Error(t('account.invalidPassword', 'Invalid password'));
      }

      // Call the anonymization function
      const { data, error } = await supabase.rpc('anonymize_user_data', {
        target_user_id: user.id
      });

      if (error) throw error;

      toast.success(t('account.anonymizeSuccess', 'Account anonymized successfully'));
      
      // Sign out and redirect
      await supabase.auth.signOut();
      window.location.href = 'https://www.uplivion.life';
      
      return data;
    } catch (error) {
      console.error('Account anonymization error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to anonymize account';
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    isDeleting,
    isExporting,
    exportUserData,
    deleteAccount,
    anonymizeAccount
  };
};